import { useEffect, useState } from 'react';

import { useToast } from '@vimeo/bokeh';
import {
  useDeleteMeCategory,
  useGetMeCategory,
  usePutMeCategory,
} from '@vimeo/gctl-api/hooks/useMeCategory';
import {
  useDeleteMeChannel,
  useGetMeChannel,
  usePutMeChannel,
} from '@vimeo/gctl-api/hooks/useMeChannel';
import {
  useDeleteMeFollowing,
  useGetMeFollowing,
  usePutMeFollowing,
} from '@vimeo/gctl-api/hooks/useMeFollowing';
import { useDeleteMeGroup, useGetMeGroup, usePutMeGroup } from '@vimeo/gctl-api/hooks/useMeGroup';
import type { Category } from '@vimeo/gctl-api/models/components/schemas/category/Category';
import type { Channel } from '@vimeo/gctl-api/models/components/schemas/channel/Channel';
import type { Group } from '@vimeo/gctl-api/models/components/schemas/group/Group';
import type { User } from '@vimeo/gctl-api/models/components/schemas/user/User';
import { translate } from '@vimeo/i18n-runtime';

type WhereType = { category?: string; channelId?: number; groupId?: number; followUserId?: number };

type UseFollowResponse = {
  isFollowingSubject?: boolean;
  isLoadingFollow: boolean;
  updateFollow: () => Promise<void>;
};

const getHooksForWhere = (where: WhereType) => {
  if (where.category !== undefined) {
    return {
      putHook: usePutMeCategory,
      deleteHook: useDeleteMeCategory,
      getHook: useGetMeCategory,
    };
  }
  if (where.channelId !== undefined) {
    return {
      putHook: usePutMeChannel,
      deleteHook: useDeleteMeChannel,
      getHook: useGetMeChannel,
    };
  }
  if (where.groupId !== undefined) {
    return {
      putHook: usePutMeGroup,
      deleteHook: useDeleteMeGroup,
      getHook: useGetMeGroup,
    };
  }
  if (where.followUserId !== undefined) {
    return {
      putHook: usePutMeFollowing,
      deleteHook: useDeleteMeFollowing,
      getHook: useGetMeFollowing,
    };
  }
  throw new Error('Invalid where type');
};

interface UseFollowOptions {
  /**
   * Initial state for the isFollowing, before the actual state is loaded from API
   */
  defaultIsFollowing?: boolean;
  /**
   * Current following state (for generic useFollow hook)
   */
  isFollowing?: boolean;
  /**
   * Can the user follow this entity?
   * Usually derived from a metadata.interaction
   */
  canFollow?: boolean;
  /**
   * Source of this follow action
   */
  source: string;
}

export const useFollow = (
  where: WhereType,
  { isFollowing, canFollow, source }: UseFollowOptions,
): UseFollowResponse => {
  const [isFollowingSubject, setIsFollowingSubject] = useState(isFollowing);

  const { putHook, deleteHook, getHook } = getHooksForWhere(where);

  const [followSubject, { loading: isPutLoading, error: putError }] = putHook();
  const [unfollowSubject, { loading: isDeleteLoading, error: deleteError }] = deleteHook();

  const {
    mutate: revalidateViewerFollowsSubject,
    isValidating,
    error: getError,
  } = getHook(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    () =>
      canFollow
        ? {
            where,
            query: { source },
          }
        : null,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  useEffect(() => {
    if (!isValidating) {
      // The GET request is unusual. It doesn't return any data. The response code would be 204 if following, and 404 if not.
      // Therefore, we have to deduce the isFollowingSubject state by existence of error.
      setIsFollowingSubject(!getError);
    }
  }, [getError, isValidating]);

  const updateFollow = async () => {
    const prevState = isFollowingSubject;
    setIsFollowingSubject(!prevState);
    const requestUpdate = prevState ? unfollowSubject : followSubject;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    await requestUpdate({ where });
    revalidateViewerFollowsSubject();
  };

  const toast = useToast();

  useEffect(() => {
    if (putError) {
      setIsFollowingSubject(false);
      toast({
        title: translate('Oops! Something went wrong. Please try again.'),
        status: 'error',
      });
    }
  }, [putError, toast]);

  useEffect(() => {
    if (deleteError) {
      setIsFollowingSubject(true);
      toast({
        title: translate('Oops! Something went wrong. Please try again.'),
        status: 'error',
      });
    }
  }, [deleteError, toast]);

  const isLoadingFollow = isValidating || isPutLoading || isDeleteLoading;
  return {
    isFollowingSubject,
    isLoadingFollow,
    updateFollow,
  };
};

function getIdFromUri(uri: string): string {
  const match = uri.match(/[a-zA-Z0-9]+$/);
  return match ? match[0] : '';
}

export function useFollowUser(user?: User | null, options?: UseFollowOptions): UseFollowResponse {
  return useFollow(
    { followUserId: Number(getIdFromUri(user?.uri ?? '')) },
    {
      isFollowing: options?.defaultIsFollowing || user?.metadata?.interactions?.follow?.added,
      canFollow: !!user && !user.metadata?.interactions?.block?.added, // This prevents any API calls when user is null
      source: options?.source ?? '',
    },
  );
}

export function useFollowChannel(
  channel?: Channel | null,
  options?: UseFollowOptions,
): UseFollowResponse {
  return useFollow(
    { channelId: Number(getIdFromUri(channel?.uri ?? '')) },
    {
      isFollowing: options?.defaultIsFollowing || channel?.metadata?.interactions?.follow?.added,
      canFollow: !!channel, // Prevent API calls when channel is null
      source: options?.source ?? '',
    },
  );
}

export function useFollowGroup(
  group?: Group | null,
  options?: UseFollowOptions,
): UseFollowResponse {
  return useFollow(
    { groupId: Number(getIdFromUri(group?.uri ?? '')) },
    {
      isFollowing: options?.defaultIsFollowing || group?.metadata?.interactions?.join?.added,
      canFollow: !!group, // Prevent API calls when group is null
      source: options?.source ?? '',
    },
  );
}

export function useFollowCategory(
  category?: Category | null,
  options?: UseFollowOptions,
): UseFollowResponse {
  return useFollow(
    { category: getIdFromUri(category?.uri ?? '') },
    {
      isFollowing: options?.defaultIsFollowing || category?.metadata?.interactions?.follow?.added,
      canFollow: !!category, // Prevent API calls when category is null
      source: options?.source ?? '',
    },
  );
}
